import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${siteUrl}/mission#webpage`,
      url: `${siteUrl}/mission`,
      name: 'Our Mission – Diatomic Life Foundation',
      description:
        "Diatomic Life Foundation's mission is to prevent and reverse oxygen deficiency through science, technology, and access — ensuring every family can breathe well.",
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Mission', item: `${siteUrl}/mission` },
        ],
      },
    },
    {
      '@type': 'NGO',
      '@id': `${siteUrl}/#organization`,
      name: 'Diatomic Life Foundation',
      url: siteUrl,
      foundingDate: '2020',
      areaServed: { '@type': 'Country', name: 'India' },
      description:
        'To prevent and reverse oxygen deficiency through education, AI-powered screening, and affordable oxygen supplementation.',
      knowsAbout: ['Oxygen Deficiency Prevention', 'AI Health Screening', 'Oxygen Supplementation', 'Community Health Education'],
    },
  ],
};

export default function Mission() {
  const navigate = useNavigate();
  
  const approaches = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'Leveraging cutting-edge AI and medical technology to make oxygen screening accessible to everyone, everywhere.'
    },
    {
      icon: 'ri-eye-line',
      title: 'Transparency',
      description: 'Open reporting on our impact, funding, and operations. Every rupee donated is accounted for and tracked.'
    },
    {
      icon: 'ri-team-line',
      title: 'Inclusion',
      description: 'Ensuring our solutions reach underserved communities, rural areas, and those who need it most.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Impact',
      description: 'Measuring real outcomes in lives improved, diseases prevented, and communities empowered.'
    }
  ];

  const values = [
    {
      icon: 'ri-shield-cross-line',
      title: 'Prevention First',
      description: 'We believe the most powerful intervention is stopping oxygen deficiency before it causes harm.'
    },
    {
      icon: 'ri-microscope-line',
      title: 'Science-Backed Approach',
      description: 'Every program, product, and initiative we run is grounded in rigorous scientific evidence and research.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Compassion-Driven Impact',
      description: 'We lead with empathy — understanding the human stories behind every health challenge we address.'
    },
    {
      icon: 'ri-global-line',
      title: 'Accessibility for All',
      description: 'Oxygen health should not be a privilege. We work to remove every barrier — financial, geographic, and informational.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Transparency in Action',
      description: 'We hold ourselves accountable through open reporting, honest communication, and measurable outcomes.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title="Our Mission | Oxygen for All – Diatomic Life Foundation"
        description="Learn about Diatomic Life Foundation's mission to prevent oxygen deficiency through innovation, transparency, inclusion, and measurable impact across India."
        keywords="oxygen mission, NGO mission India, oxygen deficiency prevention, Diatomic Life Foundation mission"
        canonical={`${siteUrl}/mission`}
        schemaJson={schema}
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block text-sm font-semibold tracking-widest text-[#12B6B6] uppercase mb-4">About Us</span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#111111] mb-8 leading-tight">
              Diatomic Life Foundation
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-10">
              A public health non-profit dedicated to addressing oxygen deficiency as a root cause of chronic disease and reduced vitality.
            </p>
            <div className="inline-block bg-[#12B6B6]/10 border border-[#12B6B6]/20 rounded-2xl px-8 py-5 mb-4">
              <p className="text-lg font-semibold text-[#12B6B6] italic">
                "Prevention begins with oxygen."
              </p>
            </div>
          </div>
        </section>

        {/* About Us Core Content */}
        <section className="py-16 bg-[#FAFAFA]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10 mb-5">
                  <i className="ri-flask-line text-2xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#111111] mb-3">Science-Grounded</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We combine science, education, and community outreach to make oxygen health understandable and reachable for every human being.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10 mb-5">
                  <i className="ri-community-line text-2xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#111111] mb-3">Community-Driven</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Our outreach programs bring oxygen literacy directly to communities — rural, urban, and underserved — where it matters most.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10 mb-5">
                  <i className="ri-shield-check-line text-2xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#111111] mb-3">Section-8 Committed</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  As a Section-8 organization, we operate with a strong commitment to transparency, accountability, and measurable impact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Crisis */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  The Crisis
                </h2>
                <div className="space-y-6 text-lg text-gray-600">
                  <p>
                    Millions of people worldwide suffer from oxygen deficiency disorders, 
                    leading to reduced quality of life, increased healthcare costs, and 
                    preventable deaths.
                  </p>
                  <p>
                    Traditional treatment methods are often expensive, inaccessible, 
                    or inadequate for addressing the root causes of oxygen deficiency.
                  </p>
                  <p>
                    In developing regions, the lack of affordable oxygen supplementation 
                    solutions creates a critical healthcare gap that affects entire communities.
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src="https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/9ae6558b7a95aafd9a820489f2c6deca.jfif" 
                  alt="The oxygen deficiency crisis" 
                  className="w-full rounded-2xl shadow-soft"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Purpose Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-16">
              To make oxygen health understandable and accessible to every individual through screening, education, and access initiatives.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="space-y-6">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-[#12B6B6]/10">
                  <i className="ri-graduation-cap-line text-3xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">Educating</h3>
                <p className="text-gray-600 leading-relaxed">
                  Raising awareness about oxygen health through comprehensive educational 
                  programs, workshops, and accessible resources for communities worldwide.
                </p>
              </div>
              <div className="space-y-6">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-[#12B6B6]/10">
                  <i className="ri-search-line text-3xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">Screening</h3>
                <p className="text-gray-600 leading-relaxed">
                  Providing AI-powered oxygen deficiency screening through our OxyAI™ app, 
                  making early detection accessible to everyone, anywhere.
                </p>
              </div>
              <div className="space-y-6">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-[#12B6B6]/10">
                  <i className="ri-heart-add-line text-3xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">Supplementing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Ensuring affordable access to oxygen supplementation through capsules, 
                  sprays, and OxyKits distributed via healthcare networks.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach Section */}
        <section className="py-20 bg-[#FAFAFA]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-8">
                Our Approach
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                We believe in principled action. Every decision we make is guided by 
                these four core values that drive our mission forward.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {approaches.map((approach, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 hover:shadow-soft transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0">
                      <i className={`${approach.icon} text-2xl text-[#12B6B6]`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#111111] mb-3">
                        {approach.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {approach.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-semibold tracking-widest text-[#12B6B6] uppercase mb-4">What We Stand For</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
                Our Values
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                These five principles guide every decision, program, and partnership we pursue.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="group bg-[#FAFAFA] rounded-2xl p-8 hover:bg-[#12B6B6] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10 group-hover:bg-white/20 mb-5 transition-colors duration-300">
                    <i className={`${value.icon} text-2xl text-[#12B6B6] group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#111111] group-hover:text-white mb-3 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/85 leading-relaxed text-sm transition-colors duration-300">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 bg-[#FAFAFA]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-8">
              Our Vision
            </h2>
            <div className="relative">
              <p className="text-2xl md:text-3xl font-medium text-gray-700 leading-relaxed mb-12">
                A world where no one suffers silently from oxygen deficiency.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
                We envision a future where oxygen health is understood, accessible, and 
                prioritized. Where communities are empowered with knowledge, technology 
                serves humanity, and every person has access to the oxygen support they need to thrive.
              </p>
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/86d046923798b542e5938a859461650c.jpg"
                  alt="Our vision for the future"
                  className="w-full rounded-2xl shadow-soft"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-[#12B6B6]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Join Our Mission
            </h2>
            <p className="text-xl text-white/90 mb-12 leading-relaxed">
              Together, we can create a world where oxygen deficiency no longer 
              threatens human health and potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/donate')}
                className="px-8 py-4 bg-white text-[#12B6B6] font-semibold rounded-full hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Support Our Cause
              </button>
              <button 
                onClick={() => navigate('/impact')}
                className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-[#12B6B6] transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
